import { useState, useEffect } from 'react';
import { CsvFieldAnalysis } from './GetFieldData';
import Loading from '../../../../components/loading/Loading';
import AlertWithIcon from '../AlertWithIcon';
import { ToInlineCommaList } from '../../lib/helpers/StringHelper';
import {
  P2GBox,
  P2GGrid,
  P2GDropdown,
  P2GCheckBoxLegacy,
  SvgConfirmCircle,
  P2GText,
  P2GButton
} from '@parcel2go/core-ui';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

interface props {
  unmapped: CsvFieldAnalysis[];
  mappable: CsvFieldAnalysis[];
  update: (fields: CsvFieldAnalysis[]) => void;
}

export const FieldMappings: React.FC<props> = (props) => {
  const [mapTo, setMapTo] = useState({});
  const [loading, setLoading] = useState(true);
  const [saveList, setSaveList] = useState<string[]>([]);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  useEffect(() => {
    const mt = { ...mapTo };
    props.unmapped.forEach((f) => {
      mt[f.index] = '';
    });
    setMapTo(mt);
    setLoading(false);
  }, []);

  const save = () => {
    props.update(
      props.unmapped.map((x) => {
        return { ...x, field: mapTo[x.index], saveMapping: saveList.includes(x.field) };
      })
    );
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <P2GBox>
      <P2GBox bordered p={2} textAlign="center">
        {props.unmapped.map((a) => (
          <P2GGrid key={a.field} container spacing={2} alignItems="center" justify="space-evenly">
            <P2GGrid item xs={6}>
              {a.field}
            </P2GGrid>
            <P2GGrid item xs={6}>
              <P2GDropdown
                value={mapTo[a.index]}
                id="Field"
                label={t('FIELD_MAPPINGS_INPUT_LABEL', { defaultValue: 'Map To' })}
                onChange={(v) => {
                  const m = { ...mapTo };
                  m[a.index] = v;
                  setMapTo(m);
                }}
                options={props.mappable
                  .map((x) => {
                    return { value: x.field, text: x.field };
                  })
                  .concat([{ value: 'IGNORE', text: 'Ignore Column' }])}
              />
            </P2GGrid>
            {mapTo[a.index] && (
              <P2GGrid item xs={12}>
                <P2GCheckBoxLegacy
                  checked={saveList.includes(a.field)}
                  label={t('SAVE_MAPPING_INPUT_LABEL', { defaultValue: 'Save Mapping' })}
                  onChange={() => {
                    if (saveList.includes(a.field)) {
                      setSaveList(saveList.filter((x) => x !== a.field));
                    } else {
                      setSaveList([...saveList, a.field]);
                    }
                  }}
                  id="Save Mapping"
                  labelPlacement="end"
                  variant="primary"
                  error={false}
                />
              </P2GGrid>
            )}
          </P2GGrid>
        ))}
      </P2GBox>
      {saveList.length > 0 && (
        <P2GBox mt={2}>
          <AlertWithIcon color="info" icon={<SvgConfirmCircle />}>
            <P2GText variant="body2">
              <Trans
                i18nKey="SAVE_MAPPING_ALERT"
                values={{ saveList: ToInlineCommaList(saveList) }}
                defaults="We'll save your mapping <strong>({{saveList}})</strong> for future uploads."
              />
            </P2GText>
          </AlertWithIcon>
        </P2GBox>
      )}
      <P2GBox mt={2}>
        <P2GButton onClick={save}>{t('CONFIRM_MAPPING_BUTTON', { defaultValue: 'Confirm Mapping' })}</P2GButton>
      </P2GBox>
    </P2GBox>
  );
};
